import * as THREE from 'three';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import type { Scene, Object3D, SkinnedMesh } from 'three';

export let sceneHelper: SceneHelper | null = null;

export function createSceneHelper(scene: Scene): SceneHelper {
  sceneHelper = new SceneHelper(scene);
  return sceneHelper;
}

export class SceneHelper {
  private _scene: Scene;
  private skinnedMeshes: SkinnedMesh[] = [];
  skySphere: THREE.Mesh | null = null;

  constructor(scene: Scene) {
    this._scene = scene;
  }

  getSkinnedMeshes(): ReadonlyArray<SkinnedMesh> {
    return this.skinnedMeshes;
  }

  rebuildSkinnedMeshes() {
    this.skinnedMeshes.length = 0;
    this._scene.traverse((obj) => {
      const mesh = obj as SkinnedMesh;
      if (!mesh.isSkinnedMesh) return;
      this.skinnedMeshes.push(mesh);
    });
  }

  getObjectByUUID(uuid: string) {
    if (!this._scene) return undefined;
    return this._scene.getObjectByProperty('uuid', uuid) as Object3D | undefined;
  }

  getScene() {
    return this._scene;
  }

  getSceneRoot() {
    return this._scene;
  }

  applyBackgroundTexture(texture: THREE.Texture, colorSpace: THREE.ColorSpace) {
    texture.mapping = THREE.UVMapping;
    texture.colorSpace = colorSpace;
    texture.needsUpdate = true;

    const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide });

    if (!this.skySphere) {
      const geometry = new THREE.SphereGeometry(500, 60, 40);
      this.skySphere = new THREE.Mesh(geometry, material);
      this.skySphere.raycast = () => {};
      this.skySphere.name = 'skySphere';
      this._scene.add(this.skySphere);
    } else {
      const oldMaterial = this.skySphere.material;
      if (!Array.isArray(oldMaterial)) oldMaterial.dispose();
      this.skySphere.material = material;
      this.skySphere.material.needsUpdate = true;
    }
  }

  async setSceneBackground(textureUrl: string, _mapping: number, colorSpace: THREE.ColorSpace) {
    if (!this._scene || !textureUrl) return;

    const isExr = textureUrl.toLowerCase().endsWith('.exr');
    const effectiveColorSpace = isExr ? THREE.LinearSRGBColorSpace : colorSpace;

    if (isExr) {
      await new EXRLoader().load(
        textureUrl,
        async (tex) => await this.applyBackgroundTexture(tex, effectiveColorSpace),
        undefined,
        (err) => console.error('EXR scene background failed:', textureUrl, err)
      );
    } else {
      await new THREE.TextureLoader().load(
        textureUrl,
        async (tex) => await this.applyBackgroundTexture(tex, effectiveColorSpace),
        undefined,
        (err) => console.error('Scene background texture failed:', textureUrl, err)
      );
    }
  }

  removeSceneBackground() {
    if (!this.skySphere) return;
    this.skySphere.visible = false;
  }
}

export function printSceneHierarchy(root: Object3D) {
  const lines: string[] = ['Scene hierarchy:'];

  function appendNode(object: Object3D, depth: number) {
    const indent = '  '.repeat(depth);
    const label = object.name ? `${object.name} (${object.type})` : object.type;
    lines.push(`${indent}${label} ${object.uuid}`);
    for (const child of object.children) {
      appendNode(child, depth + 1);
    }
  }

  appendNode(root, 0);
  console.log(lines.join('\n'));
}
